import type {
  QuoteRequest,
  QuoteResponse,
  CreateOrderPayload,
  CreateOrderResponse,
  Order,
  Template,
} from '@flashcard/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: { code: 'UNKNOWN', message: 'Unknown error' } }));
    throw new ApiError(
      res.status,
      error.error?.code || 'UNKNOWN',
      error.error?.message || `HTTP ${res.status}`,
      error.error?.details,
    );
  }
  return res.json();
}

export const api = {
  uploadFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ key: string; publicUrl: string; size: number }>('/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // multipart setzt Content-Type automatisch
    });
  },
  getQuote: (data: QuoteRequest) => request<QuoteResponse>('/quote', { method: 'POST', body: JSON.stringify(data) }),
  createOrder: (data: CreateOrderPayload) =>
    request<CreateOrderResponse>('/orders', { method: 'POST', body: JSON.stringify(data) }),
  getOrder: (id: string) => request<Order>(`/orders/${id}`),
  getTemplates: () => request<Template[]>('/templates'),
  giveConsent: (email: string, consent: boolean) =>
    request<{ success: boolean }>('/customers/consent', {
      method: 'POST',
      body: JSON.stringify({ email, consent }),
    }),
};

export type { QuoteRequest, QuoteResponse, CreateOrderPayload, CreateOrderResponse, Order, Template };
